import { z } from 'zod';

export const purchaseRequestLineSchema = z.object({
  lineId: z.string(),
  itemId: z.string(),
  itemName: z.string(),
  sku: z.string(),
  category: z.string(),
  quantity: z.number(),
  unit: z.string(),
  estimatedUnitPrice: z.number().optional(),
  estimatedTotal: z.number().optional(),
  notes: z.string().optional(),
});

export const purchaseRequestSchema = z.object({
  requestId: z.string(),
  requestNumber: z.string(),
  title: z.string(),
  description: z.string().optional(),
  status: z.enum(['draft', 'pending_approval', 'approved', 'rejected', 'sourcing', 'completed', 'cancelled']),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  siteId: z.string(),
  siteName: z.string(),
  deliveryZone: z.string(),
  requiredByDate: z.string(),
  lines: z.array(purchaseRequestLineSchema),
  estimatedTotal: z.number(),
  currency: z.string(),
  sourcingEventId: z.string().optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  approvedBy: z.string().optional(),
  approvedByName: z.string().optional(),
  approvedAt: z.string().optional(),
  rejectionReason: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PurchaseRequest = z.infer<typeof purchaseRequestSchema>;
export type PurchaseRequestLine = z.infer<typeof purchaseRequestLineSchema>;
export type PurchaseRequestStatus = PurchaseRequest['status'];
export type PurchaseRequestPriority = PurchaseRequest['priority'];

export const purchaseRequestsResponseSchema = z.object({
  data: z.array(purchaseRequestSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number(),
  }),
});

export type PurchaseRequestsResponse = z.infer<typeof purchaseRequestsResponseSchema>;

export const PR_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  sourcing: 'In Sourcing',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const PR_STATUS_COLORS: Record<PurchaseRequestStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
  sourcing: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

export const PR_PRIORITY_LABELS: Record<PurchaseRequestPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const PR_PRIORITY_COLORS: Record<PurchaseRequestPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};
